import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Brand";
import { api } from "@/lib/api";

export default function PaymentResult({ success, billing }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const team = params.get("team");
  const teamId = params.get("team_id");
  const [state, setState] = useState(success ? "checking" : "cancelled");

  useEffect(() => {
    if (!success || !sessionId) {
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setState("paid");
        if (["expired", "failed"].includes(data.payment_status)) return setState("failed");
      } catch {
        /* keep polling */
      }
      if (tries < 10) timer = setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [success, sessionId]);

  const copy = {
    checking: ["Confirming payment", "Hang tight while we check with the bank…"],
    paid: billing ? ["Plan activated", "Your new plan is live. Thanks for backing SportsDiary!"] : ["Payment received", "Thanks! Your fee is marked as paid."],
    pending: ["Still processing", "Your payment is taking longer than usual. Check back in a minute."],
    failed: ["Payment failed", "The payment didn't go through. You can try again."],
    cancelled: ["Payment cancelled", "No money was taken. You can pay any time from your player app."],
  }[state];

  return (
    <div className="flex min-h-screen flex-col items-start bg-[#0B0F17] px-6 py-8 sm:px-12">
      <Logo />
      <div className="mx-auto my-auto w-full max-w-md py-16" data-testid={`payment-state-${state}`}>
        {state === "checking" && <Loader2 className="h-10 w-10 animate-spin text-[#CCFF00]" />}
        {state === "paid" && <CheckCircle2 className="h-10 w-10 text-emerald-400" />}
        {["failed", "cancelled", "pending"].includes(state) && <XCircle className="h-10 w-10 text-amber-400" />}
        <h1 className="mt-6 font-display text-5xl font-black uppercase text-white">{copy[0]}</h1>
        <p className="mt-3 text-slate-400">{copy[1]}</p>
        <Button asChild className="mt-8 rounded-full font-bold" data-testid="payment-back-btn">
          <Link to={billing ? `/manage/${teamId}?tab=billing` : team ? `/t/${team}/players` : "/dashboard"}>{billing ? "Back to plan & billing" : "Back to player app"}</Link>
        </Button>
      </div>
    </div>
  );
}
